import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import {
	CreateGlobalDocumentTemplateDto,
	DocxFlowDocumentRes,
	DocxRenderRes,
	FieldBindingCatalogueRes,
	FieldBindingPreviewRes,
	FieldBindingRes,
	GlobalDocumentTemplateRes,
	SetTemplateAvailabilityDto,
	TemplateAvailabilityRes,
	UpdateGlobalDocumentTemplateDto,
} from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
// The state layer (`useFieldBindingState`) always produces this hand-maintained shape on
// save, so the write path takes it directly rather than the generated (enum-typed) DTO.
import type { FieldBindingDto } from '@dealstack/frontend/shared/components/FieldBinding/useFieldBindingState.types.ts';

export const getDocumentTemplates = async (
	signal?: AbortSignal
): Promise<GlobalDocumentTemplateRes[]> => {
	const response = await makeRequest<ApiV1.Response<GlobalDocumentTemplateRes[]>>(
		{ endpoint: API_PATHS.DOCUMENT_TEMPLATES_GET, method: HTTP_METHOD.GET },
		{ abortSignal: signal }
	);
	return response.payload ?? [];
};

export const getDocumentTemplate = async (
	id: string,
	signal?: AbortSignal
): Promise<GlobalDocumentTemplateRes> => {
	const response = await makeRequest<ApiV1.Response<GlobalDocumentTemplateRes>>(
		{
			endpoint: formatUnicorn(API_PATHS.DOCUMENT_TEMPLATES_ID_GET, { id }),
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	);
	const payload = response.payload;
	if (payload == null) {
		throw new Error('Document template not found');
	}
	return payload;
};

export const createDocumentTemplate = async (
	body: CreateGlobalDocumentTemplateDto
): Promise<GlobalDocumentTemplateRes> => {
	const response = await makeRequest<ApiV1.Response<GlobalDocumentTemplateRes>>({
		endpoint: API_PATHS.DOCUMENT_TEMPLATES_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	});
	const payload = response.payload;
	if (payload == null) {
		throw new Error('Document template create response missing payload');
	}
	return payload;
};

export const updateDocumentTemplate = async (
	id: string,
	body: UpdateGlobalDocumentTemplateDto
): Promise<GlobalDocumentTemplateRes> => {
	const response = await makeRequest<ApiV1.Response<GlobalDocumentTemplateRes>>({
		endpoint: formatUnicorn(API_PATHS.DOCUMENT_TEMPLATES_ID_PUT, { id }),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(body),
	});
	const payload = response.payload;
	if (payload == null) {
		throw new Error('Document template update response missing payload');
	}
	return payload;
};

export const deleteDocumentTemplate = async (id: string): Promise<void> => {
	await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(API_PATHS.DOCUMENT_TEMPLATES_ID_DELETE, { id }),
		method: HTTP_METHOD.DELETE,
	});
};

export const getTemplateAvailability = async (
	id: string,
	signal?: AbortSignal
): Promise<TemplateAvailabilityRes> => {
	const response = await makeRequest<ApiV1.Response<TemplateAvailabilityRes>>(
		{
			endpoint: formatUnicorn(API_PATHS.DOCUMENT_TEMPLATES_ID_AVAILABILITY_GET, { id }),
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	);
	const payload = response.payload;
	if (payload == null) {
		throw new Error('Template availability not found');
	}
	return payload;
};

export const updateTemplateAvailability = async (
	id: string,
	body: SetTemplateAvailabilityDto
): Promise<TemplateAvailabilityRes> => {
	const response = await makeRequest<ApiV1.Response<TemplateAvailabilityRes>>({
		endpoint: formatUnicorn(API_PATHS.DOCUMENT_TEMPLATES_ID_AVAILABILITY_PUT, { id }),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(body),
	});
	const payload = response.payload;
	if (payload == null) {
		throw new Error('Template availability update response missing payload');
	}
	return payload;
};

export const getGlobalFieldBindings = async (id: string): Promise<FieldBindingRes[]> => {
	const response = await makeRequest<ApiV1.Response<FieldBindingRes[]>>({
		endpoint: formatUnicorn(API_PATHS.DOCUMENT_TEMPLATES_ID_FIELD_BINDINGS_GET, { id }),
		method: HTTP_METHOD.GET,
	});
	return response.payload ?? [];
};

export const updateGlobalFieldBindings = async (
	id: string,
	bindings: FieldBindingDto[]
): Promise<void> => {
	await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(API_PATHS.DOCUMENT_TEMPLATES_ID_FIELD_BINDINGS_PUT, { id }),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify({ bindings }),
	});
};

// Investor-independent: always resolves against sample data, since there is no deal here.
// `securities` comes back empty for the same reason, and that is what suppresses the security
// picker in the editor.
export const getGlobalFieldBindingCatalogue = async (
	id: string
): Promise<FieldBindingCatalogueRes> => {
	const response = await makeRequest<ApiV1.Response<FieldBindingCatalogueRes>>({
		endpoint: formatUnicorn(API_PATHS.DOCUMENT_TEMPLATES_ID_FIELD_BINDING_CATALOGUE_GET, {
			id,
		}),
		method: HTTP_METHOD.GET,
	});
	return (
		response.payload ?? {
			dataPoints: [],
			securities: [],
			scopedSamples: {},
			uncollectedRequirements: [],
		}
	);
};

/** The editable reflow document behind a Word library template. */
export const getGlobalDocxFlowDocument = async (id: string): Promise<DocxFlowDocumentRes> => {
	const response = await makeRequest<ApiV1.Response<DocxFlowDocumentRes>>({
		endpoint: formatUnicorn(API_PATHS.DOCUMENT_TEMPLATES_ID_DOCX_DOCUMENT_GET, { id }),
		method: HTTP_METHOD.GET,
	});
	const payload = response.payload;
	if (payload == null) {
		throw new Error('Word document template not found');
	}
	return payload;
};

export const saveGlobalDocxFlowDocument = async (args: {
	id: string;
	document: unknown;
	bindings?: Record<string, string>;
}): Promise<DocxFlowDocumentRes> => {
	const response = await makeRequest<ApiV1.Response<DocxFlowDocumentRes>>({
		endpoint: formatUnicorn(API_PATHS.DOCUMENT_TEMPLATES_ID_DOCX_DOCUMENT_PUT, {
			id: args.id,
		}),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify({
			document: args.document,
			...(args.bindings === undefined ? {} : { bindings: args.bindings }),
		}),
	});
	const payload = response.payload;
	if (payload == null) {
		throw new Error('Word document template save response missing payload');
	}
	return payload;
};

/**
 * Renders a Word library template against the sample recipient. Always carries the editor's
 * document, so the preview reflects what is on screen rather than what is stored.
 */
export const renderGlobalDocxTemplate = async (args: {
	id: string;
	document?: unknown;
	bindings?: Record<string, string>;
	scenario?: unknown;
}): Promise<DocxRenderRes> => {
	const response = await makeRequest<ApiV1.Response<DocxRenderRes>>({
		endpoint: formatUnicorn(API_PATHS.DOCUMENT_TEMPLATES_ID_DOCX_RENDER_POST, { id: args.id }),
		method: HTTP_METHOD.POST,
		body: JSON.stringify({
			...(args.document === undefined ? {} : { document: args.document }),
			...(args.bindings === undefined ? {} : { bindings: args.bindings }),
			...(args.scenario === undefined ? {} : { scenario: args.scenario }),
		}),
	});
	const payload = response.payload;
	if (payload == null) {
		throw new Error('Word template render response missing payload');
	}
	return payload;
};

export const previewGlobalFieldBindings = async (id: string): Promise<FieldBindingPreviewRes> => {
	const response = await makeRequest<ApiV1.Response<FieldBindingPreviewRes>>({
		endpoint: formatUnicorn(API_PATHS.DOCUMENT_TEMPLATES_ID_FIELD_BINDINGS_PREVIEW_POST, {
			id,
		}),
		method: HTTP_METHOD.POST,
	});
	const payload = response.payload;
	if (payload == null) {
		throw new Error('Field-binding preview not generated');
	}
	return payload;
};
